import { useEffect, useRef, useState } from "react";

import { prefersReducedMotion, useInView } from "../hooks";
import { MOCK_MONEY, MOCK_TOURNAMENT, formatMxn } from "../mockData";
import { Avatar, MockFigure, PanelFrame, PhoneFrame, Pill, Toast } from "./frames";
import { RegistrationTop } from "./RegistrationPhone";
import "./HeroScene.css";

/**
 * M1 · Escena del encabezado de /organizador. Atrás, el panel de inscripciones
 * del organizador (cobrado, comisión y neto; enlace público; renglones con
 * pagos). Enfrente, el celular del jugador en el paso 4 («Pago»). Encima, el
 * aviso «Pago recibido», que entra deslizándose una sola vez cuando la escena
 * aparece en pantalla (sin animación con movimiento reducido).
 * A ≤860 px se oculta el panel y quedan el celular y el aviso, centrados.
 */

const PUBLIC_LINK = `torneos123.com/inscripcion/${MOCK_TOURNAMENT.slug}`;

/** Diego: Varonil Abierta B ($850) + Dobles mixto ($400) en una sola orden. */
const ORDER_LINES = [
  { label: "Varonil Abierta B", amount: 850 },
  { label: "Dobles mixto", amount: 400 },
];
const ORDER_TOTAL = ORDER_LINES.reduce((sum, line) => sum + line.amount, 0);

const ROWS = [
  { name: "Mariana Ortiz", category: "Femenil 4.0", amount: 850, paid: true, fresh: false },
  { name: "Diego Salazar", category: "Varonil Abierta B + Dobles mixto", amount: ORDER_TOTAL, paid: true, fresh: true },
  { name: "Paola Méndez", category: "Femenil 4.0", amount: 850, paid: false, fresh: false },
];

function LinkGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.2 1.2" />
      <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.2-1.2" />
    </svg>
  );
}

export function HeroScene() {
  const stageRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageRef);
  // Con movimiento reducido el aviso ya está en su lugar desde el inicio.
  const [reduced] = useState(prefersReducedMotion);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    if (inView) setPlayed(true);
  }, [inView]);

  const phase = reduced ? "" : played ? " is-in" : " is-armed";

  return (
    <MockFigure
      className="mk-hero"
      label="Ejemplo de Torneos123: un jugador paga su inscripción a la Copa Otoño 2026 desde su celular con Mercado Pago y al organizador le llega el aviso «Pago recibido», junto a su panel de inscripciones con lo cobrado, la comisión de Torneos123 y su neto."
    >
      <div ref={stageRef} className={`mk-hero__stage${phase}`}>
        <span className="mk-hero__glow" />

        <PanelFrame title={`${MOCK_TOURNAMENT.name} · Inscripciones`} className="mk-hero__panel">
          <div className="mk-hero__stats">
            <div className="mk-hero__stat">
              <span>Cobrado</span>
              <strong>{formatMxn(MOCK_MONEY.collected)}</strong>
              <span className="mk-hero__meter">
                <i style={{ width: `${MOCK_MONEY.paidPercent}%` }} />
              </span>
              <small>
                {MOCK_MONEY.paid} de {MOCK_MONEY.orders} pagados · {MOCK_MONEY.paidPercent}%
              </small>
            </div>
            <div className="mk-hero__stat">
              <span>Comisión Torneos123</span>
              <strong>{formatMxn(MOCK_MONEY.platformFee)}</strong>
              <small>
                {MOCK_MONEY.paid} × {formatMxn(99)}
              </small>
            </div>
            <div className="mk-hero__stat mk-hero__stat--net">
              <span>Neto organizador</span>
              <strong>{formatMxn(MOCK_MONEY.netBeforeMp)}</strong>
              <small>Antes de la comisión de Mercado Pago</small>
            </div>
          </div>

          <div className="mk-hero__link">
            <span className="mk-hero__linkicon">
              <LinkGlyph />
            </span>
            <span className="mk-hero__linktext">
              <small>Link público de inscripción</small>
              <b>{PUBLIC_LINK}</b>
            </span>
            <span className="mk-hero__copy">Copiar</span>
          </div>

          <ul className="mk-hero__rows">
            {ROWS.map((row) => (
              <li key={row.name} className={row.fresh ? "mk-hero__row is-fresh" : "mk-hero__row"}>
                <Avatar name={row.name} />
                <span className="mk-hero__who">
                  <strong>{row.name}</strong>
                  <small>{row.category}</small>
                </span>
                <Pill tone={row.paid ? "ok" : "warn"}>
                  {row.paid ? "Pagado" : "Pendiente"} {formatMxn(row.amount)}
                </Pill>
                {row.paid ? null : <span className="mk-hero__remind">Enviar recordatorio de pago</span>}
              </li>
            ))}
          </ul>
        </PanelFrame>

        <PhoneFrame className="mk-hero__phone">
          <RegistrationTop step={4} />

          <div className="mk-hero__stephead">
            <span>04</span>
            <div>
              <strong>Pago</strong>
              <small>Este torneo tiene pago en línea disponible.</small>
            </div>
          </div>

          <div className="mk-hero__order">
            <div className="mk-hero__buyer">
              <Avatar name="Diego Salazar" />
              <span>
                <strong>Diego Salazar</strong>
                <small>2 categorías en una sola orden</small>
              </span>
            </div>
            {ORDER_LINES.map((line) => (
              <div key={line.label} className="mk-hero__line">
                <span>{line.label}</span>
                <b>{formatMxn(line.amount)}</b>
              </div>
            ))}
            <div className="mk-hero__total">
              <span>Total a pagar</span>
              <strong>{formatMxn(ORDER_TOTAL)} MXN</strong>
            </div>
          </div>

          <span className="mk-hero__pay">Pagar en línea con Mercado Pago</span>
        </PhoneFrame>

        <Toast
          className="mk-hero__toast"
          title="Pago recibido"
          body={`Pago recibido de Diego Salazar — listo para activar en ${MOCK_TOURNAMENT.name}.`}
          when="ahora"
        />
      </div>
    </MockFigure>
  );
}
